import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable, inject} from '@angular/core';
import {tap} from 'rxjs/operators';
import {QuizActions} from './quiz.actions';
import {SessionActions} from 'src/app/shared/state/session/session.actions';
import {Quiz, QuizCompletion, QuizYearHistoryMonth} from '../models/quiz.models';
import {QuizService} from '../services/quiz.service';

export interface QuizStateModel {
  currentQuiz: Quiz | null;
  currentCompletion: QuizCompletion | null;
  yearHistoryYear: number | null;
  yearHistoryMonths: QuizYearHistoryMonth[] | null;
}

export const quizStateDefaults: QuizStateModel = {
  currentQuiz: null,
  currentCompletion: null,
  yearHistoryYear: null,
  yearHistoryMonths: null,
};

@State<QuizStateModel>({
  name: 'quiz',
  defaults: quizStateDefaults,
})
@Injectable()
export class QuizState {
  private quizService = inject(QuizService);

  @Selector([QuizState])
  static getCurrentQuiz(state: QuizStateModel): Quiz | null {
    return state.currentQuiz;
  }

  @Selector([QuizState])
  static getCurrentCompletion(state: QuizStateModel): QuizCompletion | null {
    return state.currentCompletion;
  }

  @Selector([QuizState])
  static getYearHistoryMonths(state: QuizStateModel): QuizYearHistoryMonth[] | null {
    return state.yearHistoryMonths;
  }

  @Action(QuizActions.FetchCurrent)
  fetchCurrent(ctx: StateContext<QuizStateModel>) {
    return this.quizService.fetchCurrent().pipe(
      tap(resp => {
        ctx.patchState({
          currentQuiz: resp.quiz ?? null,
          currentCompletion: resp.completion ?? null,
        });
      }),
    );
  }

  @Action(QuizActions.SubmitCompletion)
  submitCompletion(ctx: StateContext<QuizStateModel>, action: QuizActions.SubmitCompletion) {
    return this.quizService.submitCompletion(action.quizId, action.answers).pipe(
      tap(resp => {
        ctx.patchState({currentCompletion: resp.completion});
      }),
    );
  }

  @Action(QuizActions.RecordShare)
  recordShare(ctx: StateContext<QuizStateModel>, action: QuizActions.RecordShare) {
    return this.quizService.recordShare(action.completionId, action.platform).pipe(
      tap(resp => {
        const current = ctx.getState().currentCompletion;
        if (current && current.id === resp.completion_id) {
          ctx.patchState({
            currentCompletion: {
              ...current,
              has_share_entry: resp.has_share_entry,
              has_pending_share: resp.has_pending_share,
              total_entries: resp.total_entries,
            },
          });
        }
      }),
    );
  }

  @Action(QuizActions.FetchYearHistory)
  fetchYearHistory(ctx: StateContext<QuizStateModel>, action: QuizActions.FetchYearHistory) {
    return this.quizService.fetchYearHistory(action.year).pipe(
      tap(resp => {
        ctx.patchState({
          yearHistoryYear: resp.year,
          yearHistoryMonths: resp.months,
        });
      }),
    );
  }

  @Action(QuizActions.SetCurrent)
  setCurrent(ctx: StateContext<QuizStateModel>, action: QuizActions.SetCurrent) {
    ctx.patchState({
      currentQuiz: action.quiz,
      currentCompletion: action.completion,
    });
  }

  @Action(QuizActions.SetCompletion)
  setCompletion(ctx: StateContext<QuizStateModel>, action: QuizActions.SetCompletion) {
    ctx.patchState({
      currentCompletion: action.completion,
    });
  }

  @Action(QuizActions.SetYearHistory)
  setYearHistory(ctx: StateContext<QuizStateModel>, action: QuizActions.SetYearHistory) {
    ctx.patchState({
      yearHistoryYear: action.year,
      yearHistoryMonths: action.months,
    });
  }

  @Action(QuizActions.Reset)
  @Action(SessionActions.ClearState)
  reset(ctx: StateContext<QuizStateModel>) {
    ctx.setState(quizStateDefaults);
  }
}
